import h from 'hyperscript';
import $ from 'jquery';
import grayMarker from '@disy/cadenza-icons/pointer-gray.svg';
import blueMarker from '@disy/cadenza-icons/pointer-blue.svg';
import clearIcon from '@disy/cadenza-icons/clear-round.svg';
import searchActiveIcon from '@disy/cadenza-icons/search-active.svg';
import searchIcon from '@disy/cadenza-icons/search.svg';
import arrowright from '@disy/cadenza-icons/arrow-right.svg';
import { unByKey } from 'ol/Observable';
import VectorLayer from 'ol/layer/Vector';

import 'ui/progress-spinner/progress-spinner';

import * as GazetteerApi from 'cadenza/api-client/gazetteer-api';
import { logger } from 'cadenza/utils/logging';
/* necessary for progress spinner to work */
import { fitMapViewToScaledExtent } from 'cadenza/map/map-utils';
import { icon } from 'cadenza/utils/icon/icon';

import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import Style from 'ol/style/Style';
import Icon from 'ol/style/Icon';

import { MapProperty } from 'cadenza/map/map-property';

import VectorEventType from 'ol/source/VectorEventType';

import { clearAndRefreshVectorLayer } from 'cadenza/map/layer-utils';
import { mediaSmUp } from 'cadenza/utils/media';
import { GazetteerMobileModal } from 'cadenza/map/gazetteer/gazetteer-mobile-modal';
import { AbortError } from 'cadenza/api-client/abort-controller/abort-error';
import { MapFlyoutHandler } from 'cadenza/map/map-flyout-handler';
import { GazetteerInfoPanel } from 'cadenza/map/gazetteer/gazetteer-info-panel';
import {
  createGazetteerDropdown,
  isGazetteerSearchRestricted
} from 'cadenza/map/gazetteer/gazetteer-utils';
import { CustomAppMessageType, LocationSelectedOrigin } from 'cadenza/map/custom/custom-app-message-type';

import { GazetteerConfirmDialog } from './gazetteer-confirm-dialog';
import { GazetteerInteraction } from './gazetteer-interaction';
import i18n from './messages.properties';
import './gazetteer.css';

import 'jquery-ui/ui/widgets/autocomplete';

const SUGGESTIONS_DROPDOWN_CLASS = 'd-gazetteer-suggestions-dropdown';
const GOLDEN_RATIO = 0.618;
const DROPDOWN_RIGHT_MARGIN = 16;

export const ITEM_SELECTED_EVENT = 'itemSelected';
export const ITEM_SELECTED_REMOVED_EVENT = 'itemSelectedRemoved';

export class Gazetteer extends HTMLElement {

  #providersDropdownAppendElement;
  #providersDropdownWithHeading;
  #components;
  #abortController;
  #mapFlyoutHandler;
  #map;
  #placeholder;
  #title;
  #hideMarkersAfterSelect;
  #enableDropdownMenu;
  #showMobileLabel;
  #mobileLabel;

  /**
   * Configuration to customize the gazetteer component
   *
   * @typedef {object} GazetteerConfig
   * @property {string} [placeholder=''] placeholder text for empty Gazetteer
   * @property {string} [title=''] tooltip text
   * @property {boolean} [hideMarkersAfterSelect=false] if true, when an gazetteer result is selected,
   *    instead of zooming the map to the marker for the selected item, the gazetteer markers on the map are hidden.
   * @property {boolean} [enableDropdownMenu=true] if false the button and dropdown
   *    menu to select providers are not included in the component
   * @property {boolean} [showMobileLabel=true] if true the a label with the selected item label will be included
   *    in the mobile view (not desired in the toolbar one)
   * @property {HTMLElement} [appendAutocompleteMenuTo] - If specified, the autocomplete menu will be added to the specified HTML element.
   *    Otherwise the default of the JQueryUI autocomplete will be used, normally the page's body element.
   *    This is useful if the gazetteer is not part of the main stacking context, but e.g. inside a dialog.
   */

  /**
   * Constructor
   *
   * @param map - the map where the Gazetteer results are to be marked
   * @param {GazetteerConfig} [configuration] - optional configuration
   */
  constructor (map, {
    placeholder = '',
    title = '',
    hideMarkersAfterSelect = false,
    enableDropdownMenu = true,
    showMobileLabel = true,
    useCurrentBBoxByDefault = false, // use the whole map by default
    appendAutocompleteMenuTo = null,
    gazetteerConfig = {
      gazetteerProviders: [],
      useCurrentBBox: useCurrentBBoxByDefault
    }
  } = {}) {
    super();
    this._gazetteerConfig = gazetteerConfig;
    this.#map = map;
    this.#placeholder = placeholder;
    this.#title = title;
    this.#hideMarkersAfterSelect = hideMarkersAfterSelect;
    this.#enableDropdownMenu = enableDropdownMenu;
    this.#showMobileLabel = showMobileLabel;
    this.#mapFlyoutHandler = new MapFlyoutHandler(map, { closeOnOutsideClick: true });

    this.classList.add('d-gazetteer');

    // components initialization
    this.#components = [
      this._searchField = h('input.form-control.d-gazetteer-search-field', {
        type: 'text',
        placeholder: this.#placeholder,
        title: this.#title
      }),
      this._progressSpinner = h('d-progress-spinner.d-gazetteer-suggestions-progress-spinner', { hidden: true }),
      this._clearButton = h('button.button.button-icon.button-s.button-borderless.d-gazetteer-clear-button', {
        type: 'button',
        title: i18n('clearButton.title'),
        onclick: () => {
          this._clearInputFieldAndSuggestions();
        },
        hidden: true
      }, icon(clearIcon)),
      this._backButton = h('button.button.button-icon.button-borderless.d-gazetteer-back-button', {
        type: 'button',
        title: i18n('backButton.title'),
        onclick: this._onClickBackButton.bind(this),
        hidden: true
      }, icon(arrowright))
    ];

    if (this.#enableDropdownMenu) {
      this._providersButton = h('button.button.button-icon.button-borderless.button-with-dropdown.d-gazetteer-providers-button', {
        type: 'button',
        title: i18n('providersDropdown.title'),
        onclick: this._onClickProvidersButton.bind(this),
        attrs: { 'aria-expanded': false }
      }, icon(searchIcon));
      this.#components.unshift(this._providersButton);
    }

    this._onMediaSmUpChanged(mediaSmUp);

    this._initCheckedProviders();
    this._initAutocompleteComponent(this._searchField, appendAutocompleteMenuTo);

    // helper flag for handling map moveend events
    this._disabledFirstMoveEvent = false;

    const defaultStyle = createIconStyle(grayMarker, [ 24, 24 ]);
    this._highlightStyle = createIconStyle(blueMarker, [ 36, 36 ]);

    this._vectorSource = new VectorSource();
    this._vectorLayer = new VectorLayer({
      id: 'gazetteer',
      source: this._vectorSource,
      map: this.#map,
      style: defaultStyle
    });

    this._interaction = new GazetteerInteraction(this.#map, { layerFilter: layer => layer === this._vectorLayer });
    this._interaction.setActive(false);
    this.#map.addInteraction(this._interaction);
  }

  connectedCallback () {
    // Detect if waypoint was created without constructor parameters
    // (as ghost created by JQuery clone method on drag&drop with SortableJS).
    if (!this.#map) {
      return;
    }

    // events handlers
    this._eventKeys = [
      this.#map.on('moveend', this._onMovedMap.bind(this)),
      this.#map.on(`change:${MapProperty.PRIMARY_INTERACTION}`, () => this._makeSureGazetteerInteractionIsLast()),
      this.#map.getLayers().on('propertychange', event => this._updateGazetteerProviders(event)),

      ...this._vectorSource.on([ VectorEventType.ADDFEATURE, VectorEventType.REMOVEFEATURE, VectorEventType.CLEAR ], () => this._toggleInteractionActivity()),
      this._interaction.on('gazetteer.hover', e => this._handleHoverEvent(e)),
      this._interaction.on('gazetteer.click', e => this._handleClickEvent(e)),
      this._interaction.on('gazetteer.closeFlyout', _ => this.#mapFlyoutHandler.closeFlyout()),
      this._interaction.on('gazetteer.clearFlyoutHandler', _ => this.#mapFlyoutHandler.clear())
    ];

    // do not change this to mediaSmUp.AddEventListener('change', ....) - not supported on mobile safari
    mediaSmUp.addListener(this._onMediaSmUpChanged.bind(this));
  }

  _onMediaSmUpChanged ({ matches }) {
    this.innerHTML = '';
    if (matches) {
      this.append(...this.#components);
      this.#providersDropdownAppendElement = document.body;
      this.#providersDropdownWithHeading = true;
    } else {
      this.#providersDropdownWithHeading = false;
      this.append(h('button.button.button-icon.button-borderless', {
        type: 'button',
        title: i18n('gazetteerMobileButton.title'),
        onclick: () => {
          const modal = new GazetteerMobileModal(this.#components, this._clearInputFieldAndSuggestions.bind(this));
          document.body.append(modal);
          modal.addEventListener('close', () => modal.remove());
          modal.show();
          this.#providersDropdownAppendElement = modal.body;
        }
      }, icon(searchIcon)));
      this._appendMobileLabel();
    }
  }

  disconnectedCallback () {
    unByKey(this._eventKeys);
    // do not change this to mediaSmUp.removeEventListener('change', ....) - not supported on mobile safari
    mediaSmUp.removeListener(this._onMediaSmUpChanged);
  }

  _appendMobileLabel () {
    if (this.#showMobileLabel) {
      this.#mobileLabel = h('span.mobile-label');
      this.append(this.#mobileLabel);
    }
  }

  _onClickProvidersButton () {
    // if popup was open don't reopen it again
    if (this._providersButton.getAttribute('aria-expanded') === 'true') {
      return;
    }
    createGazetteerDropdown(
      this._providersButton,
      this._searchField,
      this._gazetteerConfig,
      {
        onChange: () => this._updateProvidersButton(),
        showHeader: this.#providersDropdownWithHeading
      }
    );
  }

  _updateProvidersButton () {
    const filtersActive = isGazetteerSearchRestricted(this._gazetteerConfig);
    this._providersButton.classList.toggle('search-restricted', filtersActive);
    this._providersButton.querySelector('svg').replaceWith(filtersActive ? icon(searchActiveIcon) : icon(searchIcon));
  }

  _getMapId () {
    return this.#map.get(MapProperty.ID);
  }

  _getMapSrs () {
    return this.#map.getView().getProjection().getCode();
  }

  _getMapExtent () {
    return this.#map.getView().calculateVisibleExtent();
  }

  get selectedItem () {
    return $(this._searchField).data('item');
  }

  set selectedItem (item) {
    if (item) {
      if (!item.outOfMapAreaOfUse) {
        this._selectItem(item);
      } else {
        const dialog = new GazetteerConfirmDialog(item);
        dialog.addEventListener('close', () => {
          dialog.remove();
          if (dialog.returnValue) {
            this._selectItem(item);
          } else {
            this._clearInputFieldAndSuggestions();
          }
        });
        document.body.appendChild(dialog);
        dialog.showModal();
      }
    } else {
      $(this._searchField).removeData('item');
    }

    this._clearButton.hidden = !this._searchField.value;
    this._backButton.hidden = true;
  }

  get searchText () {
    return this._searchField.value;
  }

  set searchText (text) {
    this._updateSelectedItemLabel(text);
    this._clearButton.hidden = !this._searchField.value;
  }

  _selectItem (item) {
    this._updateSelectedItemLabel(item.label);
    $(this._searchField).data('item', item);
    const feature = this._vectorSource.getFeatureById(item.id);
    clearAndRefreshVectorLayer(this._vectorLayer);
    if (feature) {
      this._vectorSource.addFeature(feature);
    }

    if (!this._gazetteerConfig.useCurrentBBox && !this.#hideMarkersAfterSelect) {
      this._displayBoundingBoxOnMap(item.bbox, item.maxZoomScale)
        .then(_ => {
          // has to happen after the zoom, because on zoom the flyout is closed automatically
          if (feature && (item.formattedAddress || item.attributes)) {
            // The promise returned by _displayBoundingBoxOnMap resolves a bit too early, when the animation
            // is not completely finished yet. The setTimeout here is needed for the flyout not to be misplaced.
            setTimeout(() => this._showInfoFlyout(feature.getGeometry().getCoordinates(), item.formattedAddress, item.attributes), 100);
            // this._showInfoFlyout(feature.getGeometry().getCoordinates(), item.address, item.attributes);
          }
        })
        .catch(error => logger.error('Failed to show gazetteer result info'));
    }

    if (this.#hideMarkersAfterSelect) {
      this._vectorSource.clear();
    }

    this._raiseItemSelectedEvent(item);
  }

  _updateSelectedItemLabel (label) {
    this._updateSearchField(label);
    this._updateMobileLabel(label);
  }

  _updateSearchField (label) {
    this._searchField.value = label;
  }

  _updateMobileLabel (label) {
    if (this.#mobileLabel) {
      this.#mobileLabel.textContent = label;
    }
  }

  _raiseItemSelectedEvent (item) {
    this.dispatchEvent(new CustomEvent(ITEM_SELECTED_EVENT, { detail: item }));
  }

  _showInfoFlyout (coordinates, formattedAddress, attributes) {
    this.#mapFlyoutHandler.displayFlyout(coordinates, new GazetteerInfoPanel(formattedAddress, attributes));
  }

  _makeSureGazetteerInteractionIsLast () {
    if (!this._interaction.getActive()) {
      return;
    }
    const interactions = this.#map.getInteractions();
    const gazetteerInteractionIndex = interactions.getArray().findIndex(i => i instanceof GazetteerInteraction);
    if (gazetteerInteractionIndex !== interactions.getLength() - 1) {
      interactions.remove(this._interaction);
      interactions.push(this._interaction);
    }
  }

  _toggleInteractionActivity () {
    const active = this._vectorSource.getFeatures().length > 0;
    this._interaction.setActive(active);
    this._makeSureGazetteerInteractionIsLast();
  }

  _onMovedMap () {
    if (this._disabledFirstMoveEvent) {
      this._disabledFirstMoveEvent = false;
    } else if (this.selectedItem && !this.#hideMarkersAfterSelect) {
      this._backButton.hidden = false;
    }
  }

  _displayBoundingBoxOnMap (boundingBox, maxZoomScale) {
    this._disabledFirstMoveEvent = true;
    // Add 20% to the bounding box, so no points are displayed close to the map view border.
    return fitMapViewToScaledExtent(this.#map, boundingBox, { maxZoomScale });
  }

  _onClickBackButton () {
    this._displayBoundingBoxOnMap(this.selectedItem.bbox, this.selectedItem.maxZoomScale);
    this._backButton.hidden = true;
  }

  _clearInputFieldAndSuggestions () {
    this._updateSelectedItemLabel(null);
    this.selectedItem = null;
    if (this.#abortController) {
      this.#abortController.abort();
    }
    clearAndRefreshVectorLayer(this._vectorLayer);
    this._raiseItemSelectedRemovedEvent();
  }

  _raiseItemSelectedRemovedEvent () {
    this.dispatchEvent(new CustomEvent(ITEM_SELECTED_REMOVED_EVENT));
  }

  /**
   * Transforms suggestions data received from REST API to format accepted by autocomplete component.
   * REST API data format is described under #Gazetteer section in API (see <a href="http://devdoc.disy.net/rest-apidoc">REST API documentation</a>)
   * <p>
   * Result autocomplete data format is described in <a href="http://api.jqueryui.com/1.11/autocomplete/#option-source">jquery documentation</a>, more:
   * as autocomplete category extension is used by gazetteer, every item must have also 'category' attribute
   * (for more information about hierarchial menu see: <a href="http://jqueryui.com/autocomplete/#categories">jquery categories example</a>) and bounding box
   * attribute (bbox) used by gazetteer component specific functionality.
   *
   * @param suggestionsDataApiFormat - data received by REST API
   * @return array of items for jquery ui autocomplete component, where item is in format {id: ..., label: ..., category: ..., bbox: ...}
   * @private
   */
  _transformSuggestions (suggestionsDataApiFormat) {
    return suggestionsDataApiFormat.suggestionsByProvider.reduce((allSuggestions, { provider, suggestions }) => {
      const maxZoomScale = provider.maxZoomScale > 0 ? provider.maxZoomScale : undefined; // maxZoomScale is 0 if not present, which breaks zooming because it try to zoom to point, undefined will lead to using default scale
      return allSuggestions.concat(suggestions.map(({ id, label, bbox, inputFieldSearchString, outOfAreaOfUse, outOfMapAreaOfUse, address, formattedAddress, attributes, marker, srsCode }) => {
        return { providerId: provider.id, id: provider.id + '-' + id, label, bbox, category: provider.label, inputFieldSearchString, outOfAreaOfUse, outOfMapAreaOfUse, address, formattedAddress, attributes, maxZoomScale, marker, srsCode };
      }));
    }, []);
  }

  _initAutocompleteComponent (searchField, appendTo) {
    this._extendAutocompleteComponent();
    $(searchField).gazetteerAutocomplete({
      position: { of: this._providersButton },
      delay: 1000,
      autoFocus: true,
      appendTo,
      source: (request, response) => {
        this._progressSpinner.hidden = false;
        const selectedProviders = this._gazetteerConfig.gazetteerProviders.filter(provider => provider.checked)
          .map(provider => provider.id);
        this.#abortController = new AbortController();
        const searchText = prepareSearchText(request.term);
        GazetteerApi.getSuggestions(
          this._getMapId(),
          this._getMapSrs(),
          this._getMapExtent(),
          searchText,
          selectedProviders,
          this._gazetteerConfig.useCurrentBBox,
          this.#abortController.signal
        ).then(result => {
          this._displaySuggestionsOnMap(result);
          if (!this._gazetteerConfig.useCurrentBBox) {
            this._zoomOutToDisplaySuggestionsIfNeeded(result);
          }
          response(this._transformSuggestions(result));
        }).catch(error => {
          if (!(error instanceof AbortError)) {
            logger.error('Failed to load gazetteer suggestions', error);
          }
        }).finally(() => { this._progressSpinner.hidden = true; });
      },
      focus: (event, ui) => {
        this._highlight(ui.item);
        return false;
      },
      select: (event, { item }) => {
        const data = { coordinate: item.marker, srsCode: item.srsCode, address: item.address, searchProviderId: item.providerId, origin: LocationSelectedOrigin.GAZETTEER };
        this.#map.getTargetElement().dispatchEvent(new CustomEvent(CustomAppMessageType.OUTGOING.LOCATION_SELECTED, { detail: data, bubbles: true }));
        this.selectedItem = item;
        this._updateSelectedItemLabel(item.inputFieldSearchString);
        return false;
      },
      open: (event, ui) => {
        const menuElement = $(event.target).data('customGazetteerAutocomplete').menu.element;
        menuElement.css('max-height', `calc((100% - ${menuElement.css('top')}) * ${GOLDEN_RATIO})`);
        if (mediaSmUp.matches) {
          // don't allow the dropdown to overflow its container, relevant only for non-mobile UI, when body element is the container.
          menuElement.css('max-width', `calc(100% - ${menuElement.css('left')} - ${DROPDOWN_RIGHT_MARGIN}px)`);
        }
      },
      close: (event, ui) => {
        if (!this.selectedItem) {
          clearAndRefreshVectorLayer(this._vectorLayer);
        }
      }
    }).on('input', () => {
      const prevSelectedItem = this.selectedItem;
      this.selectedItem = null;
      if (prevSelectedItem) {
        this.dispatchEvent(new CustomEvent(ITEM_SELECTED_REMOVED_EVENT));
      }
    });
  }

  /**
   * Creates jquery ui autocomplete component category extension, described in
   * <a href="http://jqueryui.com/autocomplete/#categories">jquery documentation</a>.
   */
  _extendAutocompleteComponent () {
    $.widget('custom.gazetteerAutocomplete', $.ui.autocomplete, {
      _create () {
        this._super();
        this.widget().menu('option', 'items', '> :not(.ui-autocomplete-category)');
        this.widget().addClass(SUGGESTIONS_DROPDOWN_CLASS);
      },
      _renderMenu (ul, items) {
        let currentCategory = '';
        items.forEach((item) => {
          if (item.category !== currentCategory) {
            ul.append(h('li.ui-autocomplete-category', item.category));
            currentCategory = item.category;
          }
          const li = this._renderItemData(ul, item);
          if (item.category) {
            li.attr('aria-label', item.category + ' : ' + item.label);
          }
        });
      }
    });
  }

  _displaySuggestionsOnMap (suggestionsDataApiFormat) {
    clearAndRefreshVectorLayer(this._vectorLayer);
    suggestionsDataApiFormat.suggestionsByProvider.forEach((item) => item.suggestions.forEach((suggestion) => {
      const coordinate = [ suggestion.marker[0], suggestion.marker[1] ];
      const feature = new Feature({
        geometry: new Point(coordinate),
        name: suggestion.label
      });
      feature.setId(item.provider.id + '-' + suggestion.id);
      this._vectorSource.addFeature(feature);
    }));
  }

  _zoomOutToDisplaySuggestionsIfNeeded (suggestionsDataApiFormat) {
    const bbox = suggestionsDataApiFormat.bbox;
    if (bbox) {
      const extent = this.#map.getView().calculateVisibleExtent();
      if (extent[0] > bbox[0] || extent[1] > bbox[1] || extent[2] < bbox[2] || extent[3] < bbox[3]) {
        this._displayBoundingBoxOnMap(bbox);
      }
    }
  }

  _highlight (item) {
    if (this._highlightedFeature) {
      this._highlightedFeature.setStyle(null);
    }
    this._highlightedFeature = this._vectorSource.getFeatureById(item.id);
    if (this._highlightedFeature) {
      this._highlightedFeature.setStyle(this._highlightStyle);
    }
  }

  _handleHoverEvent (gazetteerEvent) {
    const feature = gazetteerEvent.feature;
    const menuComponent = this._getMenuComponent();
    const itemsSelector = menuComponent.option('items');
    const menuWidget = menuComponent.widget();
    menuWidget.find(itemsSelector).each((index, value) => this._focusFromMap(gazetteerEvent, index, value, feature.getId()));
  }

  _focusFromMap (event, index, value, id) {
    const selector = $(value);
    const item = selector.data('ui-autocomplete-item');
    if (item.id === id) {
      const menuComponent = this._getMenuComponent();
      menuComponent.focus(event, selector);
      return false;
    }
  }

  _handleClickEvent (gazetteerEvent) {
    const feature = gazetteerEvent.feature;
    const menuComponent = this._getMenuComponent();
    const itemsSelector = menuComponent.option('items');
    const menuWidget = menuComponent.widget();
    menuWidget.find(itemsSelector).each((index, value) => this._selectFromMap(gazetteerEvent, index, value, feature.getId()));
  }

  _selectFromMap (event, index, value, id) {
    const selector = $(value);
    const item = selector.data('ui-autocomplete-item');
    if (item.id === id) {
      this._getMenuComponent().select({ target: value }, selector);
      return false;
    }
  }

  _getMenuComponent () {
    return $(this._searchField).data('custom-gazetteerAutocomplete').menu;
  }

  _initCheckedProviders () {
    this.#map.get(MapProperty.GAZETTEER_SUGGESTION_PROVIDERS)().then(providers => {
      if (providers.length > 0) {
        const previousProviders = this._gazetteerConfig.gazetteerProviders;

        this._gazetteerConfig.gazetteerProviders = providers.map(provider =>
          ({
            id: provider.id,
            checked: provider.initiallyChecked,
            label: provider.label
          })
        );

        // retrieve the checked state from the previous providers state
        previousProviders.forEach(oldProvider => {
          const provider = this._gazetteerConfig.gazetteerProviders.find(it => it.id === oldProvider.id);
          if (provider) {
            provider.checked = oldProvider.checked;
          }
        });
      }
    }).catch(error => {
      logger.error('Failed to initialize checked providers', error);
    });
  }

  _updateGazetteerProviders (event) {
    // update only if the number of layers has changed
    if (event?.key !== 'length') {
      return;
    }
    this.#map.get(MapProperty.GAZETTEER_SUGGESTION_PROVIDERS)()
      .then(providers => {
        // Check if some were removed
        const newProvidersIds = providers.map(provider => provider.id);
        this._gazetteerConfig.gazetteerProviders = this._gazetteerConfig.gazetteerProviders.filter(e => newProvidersIds.includes(e.id));

        // Check if there are new providers
        const currentProviderIds = this._gazetteerConfig.gazetteerProviders.map(provider => provider.id);
        providers.filter(e => !currentProviderIds.includes(e.id))
          .forEach(p => this._gazetteerConfig.gazetteerProviders.push({
            id: p.id,
            checked: p.initiallyChecked,
            label: p.label
          }));
        this._gazetteerConfig.useCurrentBBox = false; // reset to whole map, as the providers list is updated
      })
      .catch(error => {
        logger.error('Failed to update gazetteer providers', error);
      });
  }

  connectSpinner () { // used in clone
    this._progressSpinner.connectedCallback();
  }

  search (searchText, searchProviderIds) {
    this.searchText = searchText;
    this._selectProvidersById(searchProviderIds);
    const autocompleteInstance = $(this._searchField).gazetteerAutocomplete('instance');
    autocompleteInstance.search(searchText);
  }

  _selectProvidersById (providerIds) {
    if (providerIds && providerIds.length) {
      this._gazetteerConfig.gazetteerProviders.forEach(provider => {
        provider.checked = providerIds.includes(provider.id);
      });
      this._updateProvidersButton();
    }
  }

}

customElements.define('d-gazetteer', Gazetteer);

/**
 * Prepares a search term for the gazetteer search, replaces space-like characters with a standard space character.
 *
 * Java Regex does not recognize some specific space characters like "&ensp;" with '\s', but JS does it.
 * So we replace all space-like characters with a simple space here.
 *
 * We use a character "&ensp;" in the output of the coordinates under mouse cursor and capture-coordinates interaction to make the coordinates readable,
 * with just a space they are too close together. If the user has copied the coordinates, it should be possible to use them for the gazetteer search.
 *
 * @param {string} term - Search term
 * @return {string} A preprocessed term, where different space-like characters are replaced with a simple space character.
 */
function prepareSearchText (term) {
  return term?.replace(/\s/g, ' ');
}

function createIconStyle (icon, size) {
  return new Style({
    image: new Icon({
      src: 'data:image/svg+xml;base64,' + btoa(icon),
      size,
      imgSize: size,
      anchor: [ 0.5, 1 ]
    })
  });
}
